import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import ScaleOutlinedIcon from "@mui/icons-material/ScaleOutlined";
import NumbersOutlinedIcon from "@mui/icons-material/NumbersOutlined";
import { modalBoxStyle } from "../styles";
import Store from "../../../Store/Store";
import useEstablishmentUoMs from "../../../hooks/useEstablishmentUoMs";
import {
  productSizeTypes,
  unitSizeToUomSizeTypeMap,
} from "../../../clients/inventoryObjects";

const typeOptions = [
  {
    label: "Volume",
    value: productSizeTypes.Volumetric,
    icon: <WaterDropOutlinedIcon sx={{ fontSize: 16, marginRight: 1 }} />,
  },
  {
    label: "Mass",
    value: productSizeTypes.Mass,
    icon: <ScaleOutlinedIcon sx={{ fontSize: 16, marginRight: 1 }} />,
  },
  {
    label: "Count",
    value: productSizeTypes.Arbitrary,
    icon: <NumbersOutlinedIcon sx={{ fontSize: 16, marginRight: 1 }} />,
  },
];

// Shared create/edit modal for serving sizes. In edit mode the delete action sits alongside save,
// matching the trash icon on the list row.
const CreateEditServingSizeModal = ({
  isOpen,
  toggleModalOpen,
  servingSize,
  onSave,
  onDelete,
  loading,
}) => {
  const isEditing = Boolean(servingSize);
  const store = useContext(Store);
  const { data: uomData } = useEstablishmentUoMs(store.currentEstablishment?.id);

  const [name, setName] = useState("");
  const [type, setType] = useState(productSizeTypes.Volumetric);
  const [quantity, setQuantity] = useState("");
  const [unitOfMeasurement, setUnitOfMeasurement] = useState(null);

  // reset the form whenever the modal opens so a previous edit doesn't bleed into the next one
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setName(servingSize?.name ?? "");
    setType(servingSize?.unitSize?.type ?? productSizeTypes.Volumetric);
    setQuantity(
      servingSize?.unitSize?.size === null ||
        servingSize?.unitSize?.size === undefined
        ? ""
        : String(servingSize.unitSize.size)
    );
    setUnitOfMeasurement(servingSize?.unitSize?.unitOfMeasurement ?? null);
  }, [isOpen, servingSize]);

  const measurementOptions = (uomData?.unitsOfMeasurement ?? []).filter(
    (x) => !x.isKegSize && x.type === unitSizeToUomSizeTypeMap[type]
  );

  // Count is just a number of things, so there's nothing to pick a measurement for
  const measurementRequired = type !== productSizeTypes.Arbitrary;

  const handleTypeChange = (newType) => {
    if (newType === type) {
      return;
    }

    setType(newType);
    // the previous measurement belongs to the old type's list, so it can't carry over
    setUnitOfMeasurement(null);
  };

  const handleQuantityChange = (e) => {
    const { value } = e.target;

    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setQuantity(value);
    }
  };

  // the select is keyed on uom id rather than the object - a serving size loaded from the API
  // carries its own copy of the measurement, which would never match an option by reference
  const handleMeasurementChange = (e) => {
    const selected = measurementOptions.find((x) => x.id === e.target.value);
    setUnitOfMeasurement(selected ?? null);
  };

  const closeModal = () => {
    toggleModalOpen(false);
  };

  const parsedQuantity = quantity === "" ? null : Number(quantity);

  const canSave =
    name.trim().length > 0 &&
    parsedQuantity !== null &&
    parsedQuantity > 0 &&
    (!measurementRequired || unitOfMeasurement !== null);

  const handleSave = async () => {
    if (!canSave) {
      return;
    }

    await onSave({
      name: name.trim(),
      unitSize: {
        size: parsedQuantity,
        type,
        isDefaultSize: false,
        unitOfMeasurement: measurementRequired ? unitOfMeasurement : null,
      },
    });

    closeModal();
  };

  const handleDelete = async () => {
    await onDelete(servingSize);
    closeModal();
  };

  return (
    <Modal
      open={isOpen}
      onClose={closeModal}
      aria-labelledby="serving-size-modal-title"
      aria-describedby="serving-size-modal-description"
    >
      <Box sx={modalBoxStyle}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </div>
        ) : (
          <Grid container direction="column">
            <Grid
              container
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Grid item>
                <Typography variant="h2" id="serving-size-modal-title">
                  {isEditing ? "Edit Serving Size" : "Create Serving Size"}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={closeModal}>
                  <CloseOutlinedIcon />
                </IconButton>
              </Grid>
            </Grid>
            <Grid item sx={{ marginTop: 4 }}>
              {/* terrain["02"] flips with the mode - the numbered terrain scale inverts, so
                  terrain[500] reads as near-invisible dark grey in dark mode */}
              <Typography
                variant="body1"
                id="serving-size-modal-description"
                sx={{ color: (theme) => theme.palette.terrain["02"] }}
              >
                These are the preset serving sizes that will be displayed, once
                a product is added to this group. (These can be changed at a
                later day)
              </Typography>
            </Grid>

            <Box sx={{ marginTop: 6, width: "100%" }}>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                Name
              </Typography>
              <TextField
                size="small"
                required
                fullWidth
                autoComplete="off"
                placeholder="Enter serving size name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{ marginTop: 2 }}
              />
            </Box>

            <Box sx={{ marginTop: 6, width: "100%" }}>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                Type
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  marginTop: 2,
                }}
              >
                {typeOptions.map((option) => {
                  const selected = option.value === type;

                  return (
                    <Button
                      key={option.value}
                      onClick={() => handleTypeChange(option.value)}
                      // terrain "01"/"04" are the mode-aware pair - they swap in dark mode, so
                      // the selected chip stays high contrast either way. The numbered scale
                      // inverts and would render this as mid grey on black.
                      sx={{
                        flex: 1,
                        height: "40px",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: (theme) =>
                          selected
                            ? theme.palette.terrain["01"]
                            : theme.palette.terrain[300],
                        backgroundColor: (theme) =>
                          selected ? theme.palette.terrain["01"] : "transparent",
                        color: (theme) =>
                          selected
                            ? theme.palette.terrain["04"]
                            : theme.palette.terrain["01"],
                        "&:hover": {
                          backgroundColor: (theme) =>
                            selected
                              ? theme.palette.terrain["01"]
                              : theme.palette.terrain[100],
                        },
                      }}
                    >
                      {option.icon}
                      <Typography variant="smallButton">
                        {option.label}
                      </Typography>
                    </Button>
                  );
                })}
              </Box>
            </Box>

            <Box sx={{ marginTop: 6, width: "100%" }}>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                Quantity
              </Typography>
              <TextField
                size="small"
                required
                fullWidth
                autoComplete="off"
                inputProps={{ inputMode: "decimal" }}
                placeholder="Enter quantity"
                value={quantity}
                onChange={handleQuantityChange}
                sx={{ marginTop: 2 }}
              />
            </Box>

            {measurementRequired && (
              <Box sx={{ marginTop: 6, width: "100%" }}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  Measurement
                </Typography>
                <TextField
                  select
                  size="small"
                  required
                  fullWidth
                  value={
                    measurementOptions.some(
                      (x) => x.id === unitOfMeasurement?.id
                    )
                      ? unitOfMeasurement.id
                      : ""
                  }
                  onChange={handleMeasurementChange}
                  sx={{ marginTop: 2 }}
                >
                  {measurementOptions.map((uom) => (
                    <MenuItem key={uom.id} value={uom.id}>
                      {uom.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                gap: 4,
                marginTop: 8,
              }}
            >
              {isEditing && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  sx={{
                    flex: 1,
                    height: "40px",
                    borderRadius: 2,
                  }}
                >
                  <DeleteOutlineOutlinedIcon
                    sx={{ fontSize: 16, marginRight: 1 }}
                  />
                  <Typography variant="smallButton">
                    Delete Serving Size
                  </Typography>
                </Button>
              )}
              <Button
                variant="contained"
                disabled={!canSave}
                onClick={handleSave}
                sx={{
                  flex: 1,
                  height: "40px",
                  borderRadius: 2,
                }}
              >
                <Typography variant="smallButton">Save Serving Size</Typography>
              </Button>
            </Box>
          </Grid>
        )}
      </Box>
    </Modal>
  );
};

export default CreateEditServingSizeModal;
